// ═══════════════════════════════════════════════════════════════════════════════
// Admin portal datetime helpers — UI is HKT; API/storage stays UTC wall-clock
// Asia/Hong_Kong has no DST (always UTC+8).
// ═══════════════════════════════════════════════════════════════════════════════

import { DEFAULT_TIMEZONE } from '@fdh/shared';
import { parseUtcDatetime } from './formatUtcDatetime';

const HKT = DEFAULT_TIMEZONE; // Asia/Hong_Kong
const DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/;

const formatPartsInZone = (date: Date, timeZone: string): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const year = byType.year ?? '0000';
  const month = byType.month ?? '01';
  const day = byType.day ?? '01';
  const hour = byType.hour ?? '00';
  const minute = byType.minute ?? '00';
  return `${year}-${month}-${day} ${hour}:${minute}`;
};

/** Parse HKT wall-clock `YYYY-MM-DD hh:mm` to an absolute Date. */
export const parseHktDatetime = (value: string): Date | null => {
  const match = value.trim().match(DATETIME_RE);
  if (!match) {
    return null;
  }
  const [, year, month, day, hour, minute] = match;
  // HKT = UTC+8, no DST
  return new Date(
    Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour) - 8,
      Number(minute),
      0,
      0,
    ),
  );
};

/** Format an absolute Date as HKT wall-clock `YYYY-MM-DD hh:mm`. */
export const formatDateAsHkt = (date: Date): string => formatPartsInZone(date, HKT);

/** Format an absolute Date as UTC wall-clock `YYYY-MM-DD hh:mm`. */
export const formatDateAsUtcWall = (date: Date): string => formatPartsInZone(date, 'UTC');

/**
 * API UTC wall-clock → admin HKT wall-clock.
 * Empty / invalid values pass through unchanged (except trim).
 */
export const utcWallToHktWall = (value: string | null | undefined): string => {
  if (value == null) {
    return '';
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return '';
  }
  const date = parseUtcDatetime(trimmed);
  if (!date) {
    return trimmed;
  }
  return formatDateAsHkt(date);
};

/**
 * Admin HKT wall-clock → API UTC wall-clock.
 * Empty / invalid values pass through unchanged (except trim).
 */
export const hktWallToUtcWall = (value: string | null | undefined): string => {
  if (value == null) {
    return '';
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return '';
  }
  const date = parseHktDatetime(trimmed);
  if (!date) {
    return trimmed;
  }
  return formatDateAsUtcWall(date);
};

/** ISO / Date-parseable instant → HKT wall-clock for admin inputs. */
export const isoToHktWall = (value: string | null | undefined): string => {
  if (value == null) {
    return '';
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return '';
  }
  // Already wall-clock without timezone → treat as UTC wall if matches pattern
  if (DATETIME_RE.test(trimmed)) {
    return utcWallToHktWall(trimmed);
  }
  // datetime-local style without Z
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(trimmed) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed)) {
    const asUtcWall = `${trimmed.slice(0, 10)} ${trimmed.slice(11, 16)}`;
    return utcWallToHktWall(asUtcWall);
  }
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) {
    return trimmed;
  }
  return formatDateAsHkt(date);
};

/** Admin HKT wall-clock → ISO string for APIs that store timestamptz. */
export const hktWallToIso = (value: string | null | undefined): string => {
  if (value == null) {
    return '';
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return '';
  }
  const date = parseHktDatetime(trimmed);
  if (!date) {
    return trimmed;
  }
  return date.toISOString();
};

export const ADMIN_HKT_DATETIME_PLACEHOLDER = 'YYYY-MM-DD hh:mm';
